import type { BlogPost } from "@/types/content";

// General orthopaedic and patient education articles (posts 41-50).
// Article images are managed centrally in src/data/blog/images.ts (keyed by slug).
export const generalPosts: BlogPost[] = [
  {
    id: 41,
    slug: "what-does-an-orthopaedic-specialist-treat",
    title: "What Does an Orthopaedic Specialist Treat?",
    category: "Patient Education",
    excerpt: "Orthopaedic specialists care for the bones, joints, muscles and the structures that support movement.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20clean%20orthopaedic%20consultation%20desk%20with%20anatomical%20spine%2C%20knee%20and%20shoulder%20models%20and%20a%20notepad%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-41&orientation=landscape&nocache=true",
    imageAlt: "Orthopaedic specialist at a consultation desk with anatomical models",
    metaTitle: "What Does an Orthopaedic Specialist Treat? | Ortho Spine Care",
    metaDescription:
      "Learn what an orthopaedic specialist treats, from bone and joint conditions to spine and trauma care. Clear guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-06",
    updatedDate: "2026-06-10",
    readTime: "5 min read",
    tags: ["Orthopaedic", "Patient Education", "Musculoskeletal"],
    content: [
      {
        heading: "The musculoskeletal system",
        paragraphs: [
          "Orthopaedics is the branch of medicine dealing with the musculoskeletal system — the bones, joints, muscles, tendons, ligaments and nerves that allow us to move.",
        ],
      },
      {
        heading: "Conditions an orthopaedic specialist treats",
        bullets: [
          "Spine disorders causing neck and back pain",
          "Joint disorders and arthritis",
          "Tendon injuries such as rotator cuff problems",
          "Bone deformities, tumours and infections",
          "Fractures and complex trauma",
          "Sports and ligament injuries",
        ],
      },
      {
        heading: "How care usually begins",
        paragraphs: [
          "Care starts with a clinical assessment. Non-surgical treatment is considered first wherever appropriate, and surgery is advised only when it offers a clear benefit.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does an orthopaedic specialist treat spine problems?",
        a: "Yes. Spine surgery is a subspecialty within orthopaedics, and many orthopaedic surgeons, including our consultant, treat spine conditions.",
      },
      {
        q: "Should I see an orthopaedic doctor or a physiotherapist first?",
        a: "If the cause of your symptoms is unclear or they are persistent, an orthopaedic assessment is a good starting point. Physiotherapy often forms part of treatment afterwards.",
      },
    ],
    relatedServices: ["spine-disorders", "joint-disorders"],
    relatedConditions: ["arthritis", "back-pain"],
  },
  {
    id: 42,
    slug: "when-should-you-consult-an-orthopaedic-doctor",
    title: "When Should You Consult an Orthopaedic Doctor?",
    category: "Patient Education",
    excerpt: "Some bone and joint problems settle on their own. These are the signs that mean it is time to be seen.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20clean%20bright%20consultation%20room%20with%20a%20desk%2C%20chairs%20and%20anatomical%20models%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-42&orientation=landscape&nocache=true",
    imageAlt: "Patient consulting an orthopaedic doctor",
    metaTitle: "When to Consult an Orthopaedic Doctor | Ortho Spine Care",
    metaDescription:
      "Learn when an orthopaedic consultation is worthwhile, including persistent pain, swelling and injury. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-13",
    updatedDate: "2026-06-12",
    readTime: "4 min read",
    tags: ["Orthopaedic", "Patient Education", "Symptoms"],
    content: [
      {
        heading: "Reasons to book a consultation",
        bullets: [
          "Pain lasting more than a few weeks",
          "Swelling, stiffness or reduced movement in a joint",
          "Pain following an injury that is not improving",
          "Numbness, tingling or weakness in a limb",
          "A change in how you walk or use an arm",
        ],
      },
      {
        heading: "Signs that need urgent attention",
        bullets: [
          "A visibly deformed limb after an injury",
          "Inability to bear weight",
          "Fever with severe pain and swelling in a joint",
          "Loss of bladder or bowel control with back pain",
        ],
      },
      {
        heading: "Getting the most from your visit",
        paragraphs: [
          "Bring any previous reports and scans, a list of your medications, and a clear description of when your symptoms started and what makes them better or worse.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I see an orthopaedic doctor directly?",
        a: "In most cases you can request an appointment directly. Bringing previous reports helps make the consultation productive.",
      },
      {
        q: "Will I need tests at my first visit?",
        a: "Not always. Tests are arranged when they are likely to change the diagnosis or treatment plan.",
      },
    ],
    relatedServices: ["joint-disorders", "complex-bone-fracture-management"],
    relatedConditions: ["arthritis", "fractures"],
  },
  {
    id: 43,
    slug: "orthopaedic-conditions-a-patient-friendly-guide",
    title: "Orthopaedic Conditions: A Patient-Friendly Guide",
    category: "Patient Education",
    excerpt: "A plain-language overview of the orthopaedic conditions that most often bring people to a specialist.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Neat%20row%20of%20anatomical%20bone%20and%20joint%20models%20on%20a%20clean%20white%20table%20in%20a%20minimal%20study%2C%20soft%20daylight%2C%20premium%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-43&orientation=landscape&nocache=true",
    imageAlt: "Anatomical bone and joint models for patient education",
    metaTitle: "Orthopaedic Conditions: A Patient Guide | Ortho Spine Care",
    metaDescription:
      "A patient-friendly guide to common orthopaedic conditions, from arthritis and fractures to spine and tendon problems. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-20",
    updatedDate: "2026-06-14",
    readTime: "6 min read",
    tags: ["Orthopaedic", "Patient Education", "Conditions"],
    content: [
      {
        heading: "Spine conditions",
        bullets: [
          "Disc prolapse causing arm or leg pain",
          "Spinal stenosis causing heaviness in the legs when walking",
          "Spondylolisthesis, where a vertebra slips forward",
          "Spine infections and deformities",
        ],
      },
      {
        heading: "Joint and tendon conditions",
        bullets: [
          "Arthritis causing joint pain and stiffness",
          "Ligament injuries from twists and impacts",
          "Tendon injuries such as rotator cuff and heel tendon problems",
        ],
      },
      {
        heading: "Bone conditions",
        bullets: [
          "Fractures, from simple to complex",
          "Bone infections",
          "Bone deformities and tumours",
        ],
      },
      {
        heading: "Getting the right diagnosis",
        paragraphs: [
          "Many orthopaedic conditions share symptoms. An accurate diagnosis, based on history, examination and appropriate imaging, is the foundation of effective treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Are orthopaedic conditions always painful?",
        a: "Not always. Some conditions cause stiffness, weakness or reduced movement before pain becomes prominent.",
      },
      {
        q: "Can more than one orthopaedic condition be present at once?",
        a: "Yes, particularly with age. A specialist assessment identifies what is contributing to your symptoms.",
      },
    ],
    relatedServices: ["spine-disorders", "joint-disorders", "tendon-disorders"],
    relatedConditions: ["arthritis", "disc-problems", "fractures"],
  },
  {
    id: 44,
    slug: "understanding-musculoskeletal-health",
    title: "Understanding Musculoskeletal Health",
    category: "Patient Education",
    excerpt: "Musculoskeletal health is about keeping bones, joints and muscles working together throughout life.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20human%20musculoskeletal%20system%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-44&orientation=landscape&nocache=true",
    imageAlt: "Person stretching in a bright room for musculoskeletal health",
    metaTitle: "Understanding Musculoskeletal Health | Ortho Spine Care",
    metaDescription:
      "Learn what musculoskeletal health means and the habits that keep bones, joints and muscles working well. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-27",
    updatedDate: "2026-06-16",
    readTime: "5 min read",
    tags: ["Musculoskeletal", "Wellness", "Prevention"],
    content: [
      {
        heading: "What musculoskeletal health means",
        paragraphs: [
          "Musculoskeletal health is the ability of your bones, joints, muscles and connective tissues to let you move, work and live without pain or restriction. It is a major part of overall wellbeing.",
        ],
      },
      {
        heading: "What influences it",
        bullets: [
          "Regular physical activity",
          "Good nutrition, including calcium and vitamin D",
          "Maintaining a healthy weight",
          "Sleep and recovery",
          "Avoiding smoking and excess alcohol",
        ],
      },
      {
        heading: "Why it matters over a lifetime",
        paragraphs: [
          "Musculoskeletal problems are a leading cause of reduced activity and quality of life. Looking after the system early helps preserve independence and comfort as you age.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is musculoskeletal pain common?",
        a: "Yes. Back pain, joint pain and soft tissue problems are among the most common reasons people seek medical care.",
      },
      {
        q: "Can I improve musculoskeletal health at any age?",
        a: "Yes. Activity, nutrition and sensible habits continue to help at every stage of life.",
      },
    ],
    relatedServices: ["joint-disorders", "spine-disorders"],
    relatedConditions: ["arthritis", "back-pain"],
  },
  {
    id: 45,
    slug: "how-orthopaedic-diagnosis-works",
    title: "How Orthopaedic Diagnosis Works",
    category: "Patient Education",
    excerpt: "A good orthopaedic diagnosis is built from history, examination and the right tests — in that order.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Bone%20and%20joint%20X-ray%20films%20displayed%20on%20a%20modern%20illuminated%20viewing%20screen%20in%20a%20clean%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-45&orientation=landscape&nocache=true",
    imageAlt: "Doctor examining an X-ray as part of orthopaedic diagnosis",
    metaTitle: "How Orthopaedic Diagnosis Works | Ortho Spine Care",
    metaDescription:
      "Understand how orthopaedic diagnosis works, from history and examination to imaging and tests. Clear guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-03",
    updatedDate: "2026-06-18",
    readTime: "5 min read",
    tags: ["Diagnosis", "Patient Education", "Orthopaedic"],
    content: [
      {
        heading: "Step 1: History",
        paragraphs: [
          "The consultation begins with your story: when the problem started, what makes it better or worse, and how it affects daily life. This often points towards the likely cause.",
        ],
      },
      {
        heading: "Step 2: Examination",
        paragraphs: [
          "A physical examination tests movement, strength, stability and nerve function. Specific tests help identify which structure is involved.",
        ],
      },
      {
        heading: "Step 3: Investigation",
        bullets: [
          "X-rays for bones and joints",
          "MRI for soft tissues, discs and nerves",
          "CT scans in selected cases",
          "Blood tests when infection or inflammation is suspected",
        ],
      },
      {
        heading: "Why tests come last",
        paragraphs: [
          "Imaging is most useful when it answers a specific question raised by your history and examination. Using tests this way avoids unnecessary scans and keeps treatment focused.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need a scan to get a diagnosis?",
        a: "Not always. Many conditions are diagnosed from history and examination, with imaging added only when it will change the plan.",
      },
      {
        q: "How long does a diagnosis take?",
        a: "Often a working diagnosis is reached at the first consultation. If further tests are needed, the picture is completed once results are available.",
      },
    ],
    relatedServices: ["spine-disorders", "joint-disorders"],
    relatedConditions: ["back-pain", "arthritis"],
  },
  {
    id: 46,
    slug: "what-to-expect-during-an-orthopaedic-consultation",
    title: "What to Expect During an Orthopaedic Consultation",
    category: "Patient Education",
    excerpt: "Knowing what happens at a consultation helps you prepare and get the most from your visit.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Warm%20and%20clean%20clinic%20consultation%20room%20with%20two%20chairs%20and%20a%20desk%2C%20welcoming%20atmosphere%2C%20soft%20daylight%2C%20medical%20editorial%20photography%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-46&orientation=landscape&nocache=true",
    imageAlt: "Welcoming orthopaedic consultation room",
    metaTitle: "What to Expect at an Orthopaedic Consultation | Ortho Spine Care",
    metaDescription:
      "A step-by-step look at what happens during an orthopaedic consultation, from history to examination and next steps. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-10",
    updatedDate: "2026-06-20",
    readTime: "5 min read",
    tags: ["Patient Education", "Consultation", "Orthopaedic"],
    content: [
      {
        heading: "Before you arrive",
        bullets: [
          "Gather previous reports, scans and prescriptions",
          "Write down your main symptoms and when they started",
          "Note any questions you want to ask",
          "Wear or bring clothing that allows the area to be examined",
        ],
      },
      {
        heading: "During the consultation",
        paragraphs: [
          "Your specialist will take a history, examine the affected area and explain what they find. If imaging or tests are needed, the reasons will be explained clearly.",
        ],
      },
      {
        heading: "After the consultation",
        paragraphs: [
          "You will usually leave with a clearer understanding of your condition and a plan — whether that is investigations, non-surgical treatment or, in some cases, surgery. You should feel able to ask about any part of the plan.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does a consultation take?",
        a: "A first consultation usually involves a detailed history and examination, so allow a reasonable amount of time. Follow-up visits may be shorter.",
      },
      {
        q: "Can I bring someone with me?",
        a: "Yes. Many patients find it helpful to bring a family member or friend, both for support and to help remember the discussion.",
      },
    ],
    relatedServices: ["joint-disorders", "spine-disorders"],
    relatedConditions: ["arthritis", "back-pain"],
  },
  {
    id: 47,
    slug: "how-to-prepare-for-your-orthopaedic-appointment",
    title: "How to Prepare for Your Orthopaedic Appointment",
    category: "Patient Education",
    excerpt: "A little preparation makes a consultation more productive and helps your specialist help you.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20neat%20folder%20of%20medical%20reports%20and%20X-ray%20films%20with%20a%20notepad%20on%20a%20clean%20light%20table%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-47&orientation=landscape&nocache=true",
    imageAlt: "Patient organising medical reports before an appointment",
    metaTitle: "How to Prepare for Your Orthopaedic Appointment | Ortho Spine Care",
    metaDescription:
      "A practical checklist to help you prepare for your orthopaedic appointment and make the most of your consultation. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-17",
    updatedDate: "2026-06-22",
    readTime: "4 min read",
    tags: ["Patient Education", "Appointment", "Checklist"],
    content: [
      {
        heading: "Bring the right information",
        bullets: [
          "Previous X-rays, MRI or CT scans and their reports",
          "A list of current medicines and doses",
          "Details of any allergies",
          "Reports of relevant past surgery or treatment",
        ],
      },
      {
        heading: "Describe your symptoms clearly",
        bullets: [
          "When the problem started",
          "What makes it better or worse",
          "Whether pain travels into a limb",
          "How it affects work, sleep and daily activities",
        ],
      },
      {
        heading: "Prepare your questions",
        paragraphs: [
          "Write down what you most want to know — for example, whether tests are needed, what treatment options exist, and how long recovery may take. Having the list to hand means you are less likely to forget.",
        ],
      },
    ],
    faqs: [
      {
        q: "What should I wear to an orthopaedic appointment?",
        a: "Loose, comfortable clothing that allows the affected area to be examined easily is ideal.",
      },
      {
        q: "Should I stop my medicines before the appointment?",
        a: "Do not change any medication without advice. Bring your list and discuss it with your clinician.",
      },
    ],
    relatedServices: ["joint-disorders", "spine-disorders"],
    relatedConditions: ["arthritis", "back-pain"],
  },
  {
    id: 48,
    slug: "non-surgical-approaches-to-orthopaedic-care",
    title: "Non-Surgical Approaches to Orthopaedic Care",
    category: "Patient Education",
    excerpt: "Many orthopaedic problems improve without surgery. Here are the approaches that help most often.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Physiotherapy%20equipment%20including%20resistance%20bands%2C%20a%20stability%20ball%20and%20an%20exercise%20mat%20on%20a%20clean%20light%20floor%20in%20a%20bright%20rehabilitation%20room%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-48&orientation=landscape&nocache=true",
    imageAlt: "Patient receiving non-surgical physiotherapy care",
    metaTitle: "Non-Surgical Orthopaedic Care | Ortho Spine Care",
    metaDescription:
      "Learn about the non-surgical approaches to orthopaedic care, including physiotherapy, medication and injections. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-24",
    updatedDate: "2026-06-24",
    readTime: "5 min read",
    tags: ["Patient Education", "Treatment", "Conservative Care"],
    content: [
      {
        heading: "Why non-surgical care is tried first",
        paragraphs: [
          "Most orthopaedic conditions, including many back and joint problems, can be managed without an operation. Non-surgical treatment is safer and, where it works, avoids the risks of surgery.",
        ],
      },
      {
        heading: "Common non-surgical approaches",
        bullets: [
          "Activity modification and structured exercise",
          "Physiotherapy to restore movement and strength",
          "Medication for pain and inflammation",
          "Injections in selected cases",
          "Bracing or supports where appropriate",
        ],
      },
      {
        heading: "When surgery becomes the better option",
        paragraphs: [
          "If symptoms remain severe despite good non-surgical treatment, or if there is significant nerve compression or instability, surgery may offer a clearer benefit. The decision is always made together with you.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long should I try non-surgical treatment before surgery?",
        a: "It depends on the condition. Some problems need weeks of conservative care, while others need prompt surgery. Your specialist will advise for your situation.",
      },
      {
        q: "Is physiotherapy effective for back pain?",
        a: "For many people, a structured physiotherapy programme is one of the most effective treatments for persistent back pain.",
      },
    ],
    relatedServices: ["spine-disorders", "joint-disorders"],
    relatedConditions: ["back-pain", "arthritis"],
  },
  {
    id: 49,
    slug: "questions-to-ask-your-orthopaedic-specialist",
    title: "Questions to Ask Your Orthopaedic Specialist",
    category: "Patient Education",
    excerpt: "Good questions lead to better decisions. Here are some worth asking at your next appointment.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20clean%20consultation%20desk%20with%20a%20notepad%2C%20a%20pen%20and%20anatomical%20models%20in%20a%20bright%20clinic%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-49&orientation=landscape&nocache=true",
    imageAlt: "Patient asking questions during an orthopaedic consultation",
    metaTitle: "Questions to Ask Your Orthopaedic Specialist | Ortho Spine Care",
    metaDescription:
      "A helpful list of questions to ask your orthopaedic specialist so you understand your diagnosis and options. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-03",
    updatedDate: "2026-06-26",
    readTime: "4 min read",
    tags: ["Patient Education", "Consultation", "Shared Decision"],
    content: [
      {
        heading: "About your diagnosis",
        bullets: [
          "What is causing my symptoms?",
          "Is this likely to improve, stay the same or worsen?",
          "Do I need any tests, and what will they show?",
        ],
      },
      {
        heading: "About treatment",
        bullets: [
          "What are my treatment options?",
          "What happens if I choose not to treat this now?",
          "Are there non-surgical options I should try first?",
          "What are the risks and benefits of each option?",
        ],
      },
      {
        heading: "About recovery",
        bullets: [
          "How long is recovery likely to take?",
          "Will I need physiotherapy?",
          "What should I avoid while I recover?",
          "When should I come back for a review?",
        ],
      },
    ],
    faqs: [
      {
        q: "Is it okay to ask a lot of questions?",
        a: "Absolutely. Understanding your condition and options helps you make decisions that fit your life.",
      },
      {
        q: "Should I write my questions down?",
        a: "Yes. A written list helps you cover everything you want to ask and is easy for you or a companion to refer to.",
      },
    ],
    relatedServices: ["joint-disorders", "spine-disorders"],
    relatedConditions: ["arthritis", "back-pain"],
  },
  {
    id: 50,
    slug: "orthopaedic-and-spine-care-understanding-your-treatment-journey",
    title: "Orthopaedic & Spine Care: Understanding Your Treatment Journey",
    category: "Patient Education",
    excerpt: "From first consultation to recovery, here is how the treatment journey typically unfolds.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Anatomical%20spine%2C%20knee%20and%20shoulder%20models%20arranged%20on%20a%20clean%20white%20desk%20in%20a%20bright%20consultation%20room%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-50&orientation=landscape&nocache=true",
    imageAlt: "Doctor and patient at the start of an orthopaedic treatment journey",
    metaTitle: "Your Orthopaedic & Spine Treatment Journey | Ortho Spine Care",
    metaDescription:
      "A patient-friendly overview of the orthopaedic and spine treatment journey, from consultation and diagnosis to recovery. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-10",
    updatedDate: "2026-06-28",
    readTime: "5 min read",
    tags: ["Patient Education", "Treatment", "Journey"],
    content: [
      {
        heading: "Step 1: Consultation",
        paragraphs: [
          "Your journey begins with a consultation. Your specialist listens to your story, examines you and begins to build a picture of what is causing your symptoms.",
        ],
      },
      {
        heading: "Step 2: Diagnosis",
        paragraphs: [
          "Where needed, imaging or tests confirm the diagnosis. A clear diagnosis is the foundation for choosing the right treatment.",
        ],
      },
      {
        heading: "Step 3: Personalised treatment",
        bullets: [
          "Non-surgical care is considered first where appropriate",
          "Treatment is tailored to your condition and circumstances",
          "Options, benefits and risks are discussed with you",
        ],
      },
      {
        heading: "Step 4: Recovery and follow-up",
        paragraphs: [
          "Whether your treatment is non-surgical or surgical, structured follow-up and rehabilitation help you recover safely and return to the activities that matter to you.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will I always see the same specialist?",
        a: "Your care is led by the consultant, who reviews your progress at follow-up visits. You can raise any concerns at these appointments.",
      },
      {
        q: "What if my symptoms change during treatment?",
        a: "Contact the clinic. If symptoms change or worsen, your plan may need to be reviewed.",
      },
    ],
    relatedServices: ["spine-disorders", "joint-disorders", "complex-bone-fracture-management"],
    relatedConditions: ["back-pain", "arthritis", "fractures"],
  },
];